import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useContext, useEffect, useState } from 'react';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { RiskProfileVisual } from './riskProfile.visual';

const FETCH_USER = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        investmentKnowledge
        suggestedFinancialProduct {
          id
          riskProfileName { ${TRANSLATED_STRING_FIELDS} }
          riskProfileDescription { ${TRANSLATED_STRING_FIELDS} }
          children {
            id
            percentage
            financialProduct {
              id
              translatedName { ${TRANSLATED_STRING_FIELDS} }
              primaryAssetClass {
                id
                translatedName { ${TRANSLATED_STRING_FIELDS} }
                translatedDescription { ${TRANSLATED_STRING_FIELDS} }
                key
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_GOAL = gql`
  query fetchGoal($id: ObjectID!) {
    fetchGoal(goalId: $id) {
      goal {
        id
        timeHorizon
        user {
          investmentKnowledge
        }
        subAccounts {
          id
          suggestedFinancialProduct {
            id
            riskProfileName { ${TRANSLATED_STRING_FIELDS} }
            riskProfileDescription { ${TRANSLATED_STRING_FIELDS} }
            children {
              id
              percentage
              financialProduct {
                id
                translatedName { ${TRANSLATED_STRING_FIELDS} }
                primaryAssetClass {
                  id
                  translatedName { ${TRANSLATED_STRING_FIELDS} }
                  translatedDescription { ${TRANSLATED_STRING_FIELDS} }
                  key
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const RiskProfile = ({
  options,
  userId,
  onNext,
  stepLoading,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { workflowData } = useContext(WorkflowContext);
  const goalId = workflowData.currentGoalId;

  const [riskProfileData, setRiskProfileData] = useState<any>({
    riskProfileName: null,
    riskProfileDescription: null,
    children: [],
    investmentKnowledge: null,
    timeHorizon: null,
  });

  const { data: userData, loading: userDataLoading } = useQuery(FETCH_USER, {
    variables: { userId: workflowData.currentUserId ?? userId },
    fetchPolicy: 'no-cache',
    skip: !!goalId,
  });

  const { data: goalData, loading: goalDataLoading } = useQuery(FETCH_GOAL, {
    variables: { id: goalId },
    fetchPolicy: 'no-cache',
    skip: !goalId,
  });

  useEffect(() => {
    const [currentSubAccount] = goalData?.fetchGoal.goal?.subAccounts || [];
    if (!goalDataLoading && goalId && currentSubAccount && goalData) {
      setRiskProfileData({
        id: goalData.fetchGoal.goal.id,
        riskProfileName: translateBackendString(
          currentSubAccount.suggestedFinancialProduct?.riskProfileName,
        ),
        riskProfileDescription: translateBackendString(
          currentSubAccount.suggestedFinancialProduct?.riskProfileDescription,
        ),
        children: currentSubAccount.suggestedFinancialProduct?.children,
        investmentKnowledge: goalData.fetchGoal.goal.user.investmentKnowledge,
        timeHorizon: goalData.fetchGoal.goal.timeHorizon,
      });
    } else if (!userDataLoading && !goalId && userData) {
      setRiskProfileData({
        id: userData.fetchUser.user.id,
        riskProfileName: translateBackendString(
          userData.fetchUser.user.suggestedFinancialProduct?.riskProfileName,
        ),
        riskProfileDescription: translateBackendString(
          userData.fetchUser.user.suggestedFinancialProduct
            ?.riskProfileDescription,
        ),
        children: userData.fetchUser.user.suggestedFinancialProduct?.children,
        investmentKnowledge: userData.fetchUser.user.investmentKnowledge,
        timeHorizon: null,
      });
    }
  }, [userData, goalData, goalId, userId, userDataLoading, goalDataLoading]);

  return (
    <RiskProfileVisual
      options={options}
      riskProfileData={riskProfileData}
      loading={userDataLoading || goalDataLoading || stepLoading}
      continueFunc={onNext}
    />
  );
};
